import styled, { css, keyframes } from 'styled-components';

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

// Hero Section - Premium dark gradient
export const StyledPricingBox = styled.div`
  min-height: 260px;
  background: radial-gradient(ellipse 80% 50% at 50% -20%, rgba(0, 200, 224, 0.15), transparent),
    linear-gradient(180deg, #0a0f14 0%, #0f1419 40%, #141b22 100%);
  border-radius: 24px;
  border: 1px solid rgba(0, 200, 224, 0.15);
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 16px;
  padding: 56px 32px;
  width: 100%;
  box-sizing: border-box;

  @media (max-width: 768px) {
    min-height: auto;
    padding: 40px 20px;
    gap: 14px;
    border-radius: 16px;
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(
      90deg,
      transparent 0%,
      rgba(0, 200, 224, 0.3) 20%,
      rgba(0, 200, 224, 0.6) 50%,
      rgba(0, 200, 224, 0.3) 80%,
      transparent 100%
    );
  }

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: radial-gradient(circle at 20% 80%, rgba(0, 200, 224, 0.05) 0%, transparent 40%),
      radial-gradient(circle at 80% 20%, rgba(0, 200, 224, 0.08) 0%, transparent 40%);
    pointer-events: none;
  }
`;

export const HeroTitle = styled.h1`
  font-family: 'JetBrains Mono', 'SF Mono', monospace;
  font-size: 2.75rem;
  font-weight: 700;
  color: #f8fafc;
  margin: 0;
  text-align: center;
  letter-spacing: -0.03em;
  position: relative;
  z-index: 1;

  span {
    background: linear-gradient(135deg, #00d4e8 0%, #00a8b8 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  @media (max-width: 600px) {
    font-size: 1.875rem;
  }
`;

export const HeroSubtitle = styled.p`
  font-family:
    'Inter',
    -apple-system,
    sans-serif;
  font-size: 1rem;
  color: #8b9cb3;
  margin: 0;
  text-align: center;
  max-width: 460px;
  line-height: 1.7;
  position: relative;
  z-index: 1;
  font-weight: 400;
`;

export const StyledFreeButton = styled.button`
  font-family: 'JetBrains Mono', monospace;
  font-size: 0.875rem;
  font-weight: 600;
  padding: 14px 32px;
  background: linear-gradient(135deg, #00c8e0 0%, #0099ad 100%);
  border: none;
  border-radius: 10px;
  color: #0a0e14;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  z-index: 1;
  overflow: hidden;
  letter-spacing: 0.02em;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
    transition: left 0.5s ease;
  }

  &:hover {
    transform: translateY(-3px);
    box-shadow:
      0 10px 40px -10px rgba(0, 200, 224, 0.5),
      0 0 0 1px rgba(0, 200, 224, 0.2);

    &::before {
      left: 100%;
    }
  }

  &:active {
    transform: translateY(-1px);
  }
`;

// Plans Grid
export const PlansGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 24px;
  margin-top: 40px;
  width: 100%;
  padding: 0;
  box-sizing: border-box;
  align-items: stretch;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 20px;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 20px;
    max-width: 420px;
    margin-left: auto;
    margin-right: auto;
  }
`;

// Plan Card - Premium design
export const StyledAreaBox = styled.div<{ $active?: boolean; $popular?: boolean; $isDark?: boolean }>`
  width: 100%;
  min-width: 0;
  background: ${props =>
    props.$isDark
      ? 'linear-gradient(180deg, #151c24 0%, #121920 100%)'
      : 'linear-gradient(180deg, #ffffff 0%, #fafbfc 100%)'};
  border: 1px solid
    ${props =>
      props.$active
        ? 'rgba(0, 255, 127, 0.5)'
        : props.$popular
          ? 'rgba(0, 200, 224, 0.4)'
          : props.$isDark
            ? 'rgba(42, 53, 68, 0.6)'
            : 'rgba(0, 0, 0, 0.08)'};
  border-radius: 20px;
  overflow: hidden;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  animation: ${fadeInUp} 0.6s ease-out backwards;
  position: relative;
  display: flex;
  flex-direction: column;

  ${props =>
    props.$active &&
    css`
      box-shadow:
        0 0 0 1px rgba(0, 255, 127, 0.2),
        0 20px 40px -20px rgba(0, 255, 127, 0.3);
    `}

  ${props =>
    props.$popular &&
    !props.$active &&
    css`
      border-color: rgba(0, 200, 224, 0.5);
      box-shadow:
        0 0 0 1px rgba(0, 200, 224, 0.1),
        0 25px 50px -20px rgba(0, 200, 224, 0.25);
      transform: scale(1.02);
      z-index: 2;

      @media (max-width: 768px) {
        transform: scale(1);
      }
    `}

  ${props =>
    !props.$popular &&
    !props.$active &&
    css`
      box-shadow: ${props.$isDark ? '0 4px 20px rgba(0, 0, 0, 0.3)' : '0 4px 20px rgba(0, 0, 0, 0.06)'};
    `}

  &:hover {
    transform: ${props => (props.$popular ? 'scale(1.04) translateY(-4px)' : 'translateY(-6px)')};
    border-color: ${props => (props.$active ? 'rgba(0, 255, 127, 0.6)' : 'rgba(0, 200, 224, 0.5)')};
    box-shadow: ${props =>
      props.$active ? '0 20px 50px -15px rgba(0, 255, 127, 0.4)' : '0 20px 50px -15px rgba(0, 200, 224, 0.3)'};

    @media (max-width: 768px) {
      transform: translateY(-4px);
    }
  }
`;

// Popular Badge - Chip style matching ActiveChip
export const PopularRibbon = styled.span`
  font-family: 'JetBrains Mono', monospace;
  font-size: 0.625rem;
  padding: 5px 12px;
  background: linear-gradient(135deg, #00c8e0 0%, #0099ad 100%);
  border: 1px solid rgba(0, 200, 224, 0.5);
  border-radius: 20px;
  color: #ffffff;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  box-shadow: 0 2px 8px rgba(0, 200, 224, 0.3);

  &::before {
    content: 'MOST POPULAR';
  }
`;

export const StyledColoredBox = styled.div<{ $bgColor?: string; $isDark?: boolean; $isPopular?: boolean }>`
  padding: 28px 24px 24px;
  min-height: 320px;
  background: ${props => {
    if (props.$isPopular) {
      return props.$isDark
        ? 'linear-gradient(135deg, rgba(0, 200, 224, 0.12) 0%, rgba(0, 200, 224, 0.04) 100%)'
        : 'linear-gradient(135deg, rgba(0, 200, 224, 0.08) 0%, rgba(0, 200, 224, 0.02) 100%)';
    }
    return props.$isDark ? props.$bgColor || 'rgba(0, 200, 224, 0.04)' : props.$bgColor || 'rgba(0, 0, 0, 0.01)';
  }};
  border-bottom: 1px solid ${props => (props.$isDark ? 'rgba(42, 53, 68, 0.4)' : 'rgba(0, 0, 0, 0.06)')};
  position: relative;
  display: flex;
  flex-direction: column;

  @media (max-width: 768px) {
    min-height: auto;
  }
`;

export const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

export const CardTitle = styled.h3<{ $isDark?: boolean }>`
  font-family: 'JetBrains Mono', monospace;
  font-size: 1.375rem;
  font-weight: 600;
  color: ${props => (props.$isDark ? '#f0f4f8' : '#0f172a')};
  margin: 0;
  letter-spacing: -0.01em;
`;

export const ActiveChip = styled.span`
  font-family: 'JetBrains Mono', monospace;
  font-size: 0.625rem;
  padding: 5px 12px;
  background: linear-gradient(135deg, rgba(0, 200, 127, 0.2), rgba(0, 200, 127, 0.1));
  border: 1px solid rgba(0, 200, 127, 0.3);
  border-radius: 20px;
  color: #00c87f;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
`;

export const ExpiredChip = styled.span`
  font-family: 'JetBrains Mono', monospace;
  font-size: 0.625rem;
  padding: 5px 12px;
  background: rgba(255, 71, 87, 0.1);
  border: 1px solid rgba(255, 71, 87, 0.3);
  border-radius: 20px;
  color: #ff4757;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
`;

// Features List
export const StyledList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
`;

export const StyledListItem = styled.li<{ $isDark?: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 0;
  font-family:
    'Inter',
    -apple-system,
    sans-serif;
  font-size: 0.875rem;
  color: ${props => (props.$isDark ? '#94a3b8' : '#64748b')};
  line-height: 1.5;
  transition: color 0.2s ease;

  &::before {
    content: '';
    width: 18px;
    height: 18px;
    background: linear-gradient(135deg, rgba(0, 200, 224, 0.2) 0%, rgba(0, 200, 224, 0.1) 100%);
    border-radius: 50%;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background-image: url("data:image/svg+xml,%3Csvg width='10' height='8' viewBox='0 0 10 8' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 4L3.5 6.5L9 1' stroke='%2300c8e0' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: center;
  }
`;

// Card Body
export const CardBody = styled.div<{ $isDark?: boolean }>`
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  background: ${props => (props.$isDark ? 'transparent' : 'transparent')};
  flex: 1;
`;

export const PriceRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
`;

export const PriceText = styled.span<{ $isDark?: boolean }>`
  font-family: 'JetBrains Mono', monospace;
  font-size: 2rem;
  font-weight: 700;
  color: ${props => (props.$isDark ? '#f0f4f8' : '#0f172a')};
  letter-spacing: -0.03em;
  line-height: 1.1;
`;

export const PriceCaption = styled.span<{ $isDark?: boolean }>`
  display: block;
  font-family:
    'Inter',
    -apple-system,
    sans-serif;
  font-size: 0.75rem;
  font-weight: 500;
  color: ${props => (props.$isDark ? '#64748b' : '#94a3b8')};
  letter-spacing: 0.02em;
  margin-top: 4px;
`;

export const OriginalPrice = styled.span<{ $isDark?: boolean }>`
  display: inline-block;
  font-family: 'JetBrains Mono', monospace;
  font-size: 0.95rem;
  font-weight: 500;
  color: ${props => (props.$isDark ? '#64748b' : '#94a3b8')};
  text-decoration: line-through;
  text-decoration-thickness: 1.5px;
  text-decoration-color: ${props => (props.$isDark ? 'rgba(255, 71, 87, 0.55)' : 'rgba(255, 71, 87, 0.5)')};
  margin-top: 6px;
  letter-spacing: -0.01em;
  opacity: 0.85;
`;

export const SavingsLine = styled.span<{ $isDark?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-family: 'JetBrains Mono', monospace;
  font-size: 0.78rem;
  font-weight: 600;
  color: ${props => (props.$isDark ? '#00ff7f' : '#00a85a')};
  letter-spacing: 0.02em;
  margin-top: 6px;
  text-shadow: ${props => (props.$isDark ? '0 0 14px rgba(0, 255, 127, 0.25)' : 'none')};

  &::before {
    content: '';
    display: inline-block;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: currentColor;
    box-shadow: 0 0 8px currentColor;
  }
`;

export const PriceBlock = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

export const CancelButton = styled.button`
  padding: 8px;
  background: transparent;
  border: 1px solid rgba(255, 71, 87, 0.2);
  border-radius: 8px;
  color: #ff4757;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: rgba(255, 71, 87, 0.1);
    border-color: rgba(255, 71, 87, 0.4);
    transform: scale(1.05);
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

export const StyledChip = styled.div<{ $isDark?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: ${props => (props.$isDark ? 'rgba(0, 200, 224, 0.08)' : 'rgba(0, 200, 224, 0.06)')};
  border: 1px solid ${props => (props.$isDark ? 'rgba(0, 200, 224, 0.15)' : 'rgba(0, 200, 224, 0.2)')};
  border-radius: 10px;
  font-family: 'JetBrains Mono', monospace;
  font-size: 0.8125rem;
  font-weight: 500;
  color: ${props => (props.$isDark ? '#00b8d0' : '#0099ad')};
`;

export const EnterpriseText = styled.p<{ $isDark?: boolean }>`
  font-family:
    'Inter',
    -apple-system,
    sans-serif;
  font-size: 0.875rem;
  color: ${props => (props.$isDark ? '#8b9cb3' : '#64748b')};
  margin: 0;
  line-height: 1.6;
`;

// Buttons
export const PrimaryButton = styled.button<{ $disabled?: boolean; $isPopular?: boolean }>`
  width: 100%;
  font-family: 'JetBrains Mono', monospace;
  font-size: 0.875rem;
  font-weight: 600;
  padding: 14px 24px;
  background: ${props =>
    props.$disabled ? 'rgba(100, 116, 139, 0.2)' : 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)'};
  border: ${props => (props.$disabled ? 'none' : '1px solid rgba(0, 200, 224, 0.3)')};
  border-radius: 12px;
  color: ${props => (props.$disabled ? '#64748b' : '#ffffff')};
  cursor: ${props => (props.$disabled ? 'not-allowed' : 'pointer')};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
  letter-spacing: 0.02em;
  margin-top: auto;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.1), transparent);
    transition: left 0.5s ease;
  }

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 10px 30px -10px rgba(0, 0, 0, 0.3);

    &::before {
      left: 100%;
    }
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }
`;

export const ActiveButton = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  font-family: 'JetBrains Mono', monospace;
  font-size: 0.875rem;
  font-weight: 600;
  padding: 14px 24px;
  background: linear-gradient(135deg, rgba(0, 200, 127, 0.15), rgba(0, 200, 127, 0.08));
  border: 1px solid rgba(0, 200, 127, 0.25);
  border-radius: 12px;
  color: #00c87f;
  cursor: default;
  margin-top: auto;
  letter-spacing: 0.02em;

  svg {
    width: 18px;
    height: 18px;
  }
`;

export const StyledBlackButton = styled.button`
  width: 100%;
  font-family: 'JetBrains Mono', monospace;
  font-size: 0.875rem;
  font-weight: 600;
  padding: 14px 24px;
  background: linear-gradient(135deg, #1e293b 0%, #0f172a 100%);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  color: #ffffff;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  letter-spacing: 0.02em;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 30px -10px rgba(0, 0, 0, 0.4);
    border-color: rgba(0, 200, 224, 0.3);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

// Toggle Container
export const ToggleContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 40px;
`;
